import { useState, useMemo, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { TimeOff } from '../../services/api/timeOff';

interface TimeOffCalendarProps {
  timeOffs: TimeOff[];
  currentUserId?: number;
  onTimeOffClick?: (timeOffId: number) => void;
  /** Fired on double-click — used to trigger inline edit from the calendar (MKS-97). */
  onTimeOffDoubleClick?: (timeOffId: number) => void;
  /** Show status-based styling for all time-offs (manager view) instead of just own */
  showStatusForAll?: boolean;
  /** When set, navigates the calendar to this month/year */
  navigateToMonth?: { month: number; year: number } | null;
  /** When set, highlights spans for this time-off ID in yellow */
  highlightedTimeOffId?: number | null;
}

interface CalendarDay {
  date: string;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  gridIndex: number;
}

interface TimeOffSpan {
  id: string; // unique identifier for this span
  timeOffId: number;
  userName: string;
  typeName: string | null;
  /** MKS-327 — the type's own colour, shown as the span's left edge. */
  colour: string;
  /** MKS-327 — 'presence' means the person is working, just elsewhere. */
  isPresence: boolean;
  state: string;
  isOwn: boolean;
  startIndex: number; // index in calendarDays array
  endIndex: number; // index in calendarDays array (inclusive)
  track: number; // vertical position (0 = first row)
}

/** Matches the `colour` column default in schema.prisma. */
const DEFAULT_TIME_OFF_COLOUR = '#6366F1';

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getMonthName(month: number): string {
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return names[month] || 'Unknown';
}

export function TimeOffCalendar({ timeOffs, currentUserId, onTimeOffClick, onTimeOffDoubleClick, showStatusForAll = false, navigateToMonth, highlightedTimeOffId }: TimeOffCalendarProps) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  useEffect(() => {
    if (navigateToMonth) {
      setCurrentMonth(navigateToMonth.month);
      setCurrentYear(navigateToMonth.year);
    }
  }, [navigateToMonth]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // Generate calendar days
  const calendarDays = useMemo((): CalendarDay[] => {
    const days: CalendarDay[] = [];
    let gridIndex = 0;

    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);

    let startDayOfWeek = firstDay.getDay();
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    // Add days from previous month
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(prevMonthLastDay);
      date.setDate(prevMonthLastDay.getDate() - i);
      days.push({
        date: formatDate(date),
        dayOfMonth: date.getDate(),
        isCurrentMonth: false,
        gridIndex: gridIndex++,
      });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      days.push({
        date: formatDate(date),
        dayOfMonth: day,
        isCurrentMonth: true,
        gridIndex: gridIndex++,
      });
    }

    // Add days from next month
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(currentYear, currentMonth + 1, i);
      days.push({
        date: formatDate(date),
        dayOfMonth: i,
        isCurrentMonth: false,
        gridIndex: gridIndex++,
      });
    }

    return days;
  }, [currentMonth, currentYear]);

  // Create time-off spans
  const timeOffSpans = useMemo((): TimeOffSpan[] => {
    const spans: TimeOffSpan[] = [];

    for (const timeOff of timeOffs) {
      const isOwn = currentUserId != null && timeOff.userId === currentUserId;

      // Get user name
      let userName = 'Unknown';
      if (timeOff.user) {
        if (timeOff.user.firstName && timeOff.user.lastName) {
          userName = `${timeOff.user.firstName} ${timeOff.user.lastName}`;
        } else {
          userName = timeOff.user.email;
        }
      }

      // Use dateFrom/dateTo for full range (days array only has workdays)
      let firstDate: string | undefined;
      let lastDate: string | undefined;

      if (timeOff.dateFrom && timeOff.dateTo) {
        firstDate = timeOff.dateFrom;
        lastDate = timeOff.dateTo;
      } else {
        // Fallback to days array
        const dates = timeOff.days
          .filter(day => day.date)
          .map(day => day.date!)
          .sort();

        if (dates.length === 0) continue;
        firstDate = dates[0];
        lastDate = dates[dates.length - 1];
      }

      if (!firstDate || !lastDate) continue;

      // Find start and end indices in calendar
      const startIdx = calendarDays.findIndex(d => d.date === firstDate);
      const endIdx = calendarDays.findIndex(d => d.date === lastDate);

      if (startIdx === -1 || endIdx === -1) continue;

      // Create spans for the full range, breaking only at week boundaries
      let currentStart = startIdx;

      while (currentStart <= endIdx) {
        const currentWeekEnd = Math.floor(currentStart / 7) * 7 + 6; // last day of this week
        const spanEnd = Math.min(endIdx, currentWeekEnd);

        spans.push({
          id: `${timeOff.id}-${currentStart}-${spanEnd}`,
          timeOffId: timeOff.id,
          userName,
          typeName: timeOff.timeOffType?.name ?? null,
          colour: timeOff.timeOffType?.colour ?? DEFAULT_TIME_OFF_COLOUR,
          isPresence: timeOff.timeOffType?.category === 'presence',
          state: timeOff.state,
          isOwn,
          startIndex: currentStart,
          endIndex: spanEnd,
          track: 0,
        });

        currentStart = spanEnd + 1; // start of next week
      }
    }

    // Sort so own spans come first (get lower tracks = rendered on top)
    spans.sort((a, b) => {
      if (a.isOwn !== b.isOwn) return a.isOwn ? -1 : 1;
      return 0;
    });

    // Assign tracks to prevent overlaps
    assignTracks(spans);

    return spans;
  }, [timeOffs, calendarDays, currentUserId]);

  // Calculate dynamic row heights based on max tracks per week
  const rowHeights = useMemo((): Map<number, number> => {
    const heights = new Map<number, number>();
    const minHeight = 100;
    const baseHeight = 28; // space for day number
    const trackHeight = 20; // height per track

    // Find max track for each week
    for (const span of timeOffSpans) {
      const weekRow = Math.floor(span.startIndex / 7);
      const currentMax = heights.get(weekRow) || 0;
      const requiredHeight = baseHeight + ((span.track + 1) * trackHeight);
      heights.set(weekRow, Math.max(currentMax, requiredHeight, minHeight));
    }

    // Ensure all 6 weeks have a height
    for (let week = 0; week < 6; week++) {
      if (!heights.has(week)) {
        heights.set(week, minHeight);
      }
    }

    return heights;
  }, [timeOffSpans]);

  const todayStr = formatDate(today);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {getMonthName(currentMonth)} {currentYear}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={handleToday}
            className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            Today
          </button>
          <button
            onClick={handleNextMonth}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            aria-label="Next month"
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="relative">
        <div className="grid grid-cols-7 gap-1">
          {/* Weekday headers */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day) => {
            const isToday = day.date === todayStr;
            const weekRow = Math.floor(day.gridIndex / 7);
            const rowHeight = rowHeights.get(weekRow) || 100;

            return (
              <div
                key={day.gridIndex}
                className={`border border-gray-100 rounded p-1 ${
                  day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                }`}
                style={{ minHeight: `${rowHeight}px` }}
              >
                <div
                  className={`text-sm font-medium mb-1 ${
                    isToday
                      ? 'bg-primary-600 text-white rounded-full w-6 h-6 flex items-center justify-center'
                      : day.isCurrentMonth
                        ? 'text-gray-900'
                        : 'text-gray-400'
                  }`}
                >
                  {day.dayOfMonth}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time-off spans (absolute positioned over the grid) */}
        {timeOffSpans.map((span) => {
            let bgColor: string;
            let textColor: string;
            let borderStyle: string;

            // Show status-based styling if it's own time-off OR if showStatusForAll is enabled (manager view)
            const showStatusStyling = span.isOwn || showStatusForAll;

            if (!showStatusStyling) {
              // Team time-offs in employee view: light blue, not clickable
              bgColor = 'bg-blue-50';
              textColor = 'text-blue-600';
              borderStyle = 'border border-blue-200';
            } else if (span.state === 'approved') {
              bgColor = 'bg-green-100';
              textColor = 'text-green-800';
              borderStyle = 'border border-green-300';
            } else if (span.state === 'rejected') {
              bgColor = 'bg-red-100';
              textColor = 'text-red-800';
              borderStyle = 'border border-red-300';
            } else if (span.state === 'submitted') {
              bgColor = 'bg-white';
              textColor = 'text-gray-700';
              borderStyle = 'border-2 border-dashed border-gray-400';
            } else {
              bgColor = 'bg-white';
              textColor = 'text-gray-700';
              borderStyle = 'border-2 border-dashed border-gray-300';
            }

            // Calculate positions
            const weekRow = Math.floor(span.startIndex / 7);
            const startCol = span.startIndex % 7;
            const spanLength = span.endIndex - span.startIndex + 1;

            // Calculate pixel positions with dynamic row heights
            const headerHeight = 36;
            const gap = 4;

            // Calculate cumulative top position
            let topPosition = headerHeight;
            for (let w = 0; w < weekRow; w++) {
              topPosition += (rowHeights.get(w) || 100) + gap;
            }
            topPosition += 28 + (span.track * 20); // offset for day number + track

            // CSS Grid distributes columns as: (container_width - total_gaps) / 7
            // Total gaps = 6 * 4px = 24px (6 gaps between 7 columns)
            const totalGaps = 6 * gap;
            const padding = 7;
            const left = `calc(${startCol} * (100% - ${totalGaps}px) / 7 + ${startCol * gap}px + ${padding}px)`;
            const width = `calc(${spanLength} * (100% - ${totalGaps}px) / 7 + ${(spanLength - 1) * gap}px - ${padding * 2}px)`;

            // MKS-327: a presence day is WORK, so the calendar has to say so —
            // "Conference (approved)" alone reads as an absence to anyone
            // scanning the month for who is around.
            const presenceSuffix = span.isPresence ? ' - working' : '';
            const title = showStatusStyling
              ? `${span.userName} - ${span.typeName || 'Time Off'} (${span.state})${presenceSuffix}`
              : `${span.userName}${presenceSuffix}`;

            // The type's own colour rides on the left edge rather than the
            // fill: the fill already carries the approval state, which the
            // manager view depends on.
            const typeAccent = { borderLeft: `4px solid ${span.colour}` };

            // Override with yellow highlight if this span is the selected one
            if (highlightedTimeOffId != null && span.timeOffId === highlightedTimeOffId) {
              bgColor = 'bg-yellow-200';
              textColor = 'text-yellow-900';
              borderStyle = 'border-2 border-yellow-400';
            }

            // Make clickable if it's own time-off OR if showStatusForAll is enabled (manager view)
            const isClickable = span.isOwn || showStatusForAll;

            if (!isClickable) {
              // Non-clickable div for team time-offs in employee view
              return (
                <div
                  key={span.id}
                  className={`${bgColor} ${textColor} ${borderStyle} rounded px-1 py-0.5 text-[10px] leading-tight truncate text-left absolute`}
                  style={{
                    top: `${topPosition}px`,
                    left,
                    width,
                    height: '18px',
                    zIndex: 10 + span.track,
                    ...typeAccent,
                  }}
                  title={title}
                >
                  {span.userName}
                  {span.isPresence && <span className="ml-1 opacity-70">· working</span>}
                </div>
              );
            }

            return (
              <button
                key={span.id}
                onClick={() => onTimeOffClick?.(span.timeOffId)}
                onDoubleClick={() => onTimeOffDoubleClick?.(span.timeOffId)}
                className={`${bgColor} ${textColor} ${borderStyle} rounded px-1 py-0.5 text-[10px] leading-tight truncate text-left hover:opacity-80 transition-opacity cursor-pointer absolute`}
                style={{
                  top: `${topPosition}px`,
                  left,
                  width,
                  height: '18px',
                  zIndex: 10 + span.track,
                  ...typeAccent,
                }}
                title={title}
              >
                {span.userName}
                {span.isPresence && <span className="ml-1 opacity-70">· working</span>}
              </button>
            );
          })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-600">
        {/* MKS-327: the left edge of a span is its type's colour, and a
            presence type is work rather than an absence. */}
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-white border border-gray-300 border-l-4 border-l-teal-500 rounded"></div>
          <span>Type colour · &ldquo;working&rdquo; = present, elsewhere</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
          <span>Approved</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-white border-2 border-dashed border-gray-400 rounded"></div>
          <span>Submitted</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-white border-2 border-dashed border-gray-300 rounded"></div>
          <span>Open</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
          <span>Rejected</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
          <span>Team</span>
        </div>
      </div>
    </div>
  );
}

// Assign tracks to spans to avoid overlaps
function assignTracks(spans: TimeOffSpan[]): void {
  // Sort by start index, then by length (longer spans first)
  const sortedSpans = [...spans].sort((a, b) => {
    if (a.startIndex !== b.startIndex) {
      return a.startIndex - b.startIndex;
    }
    return (b.endIndex - b.startIndex) - (a.endIndex - a.startIndex);
  });

  // Track occupation: for each week row, track which columns are occupied at each track level
  const weekTracks = new Map<number, Array<Set<number>>>();

  for (const span of sortedSpans) {
    const weekRow = Math.floor(span.startIndex / 7);

    if (!weekTracks.has(weekRow)) {
      weekTracks.set(weekRow, []);
    }

    const tracks = weekTracks.get(weekRow)!;

    // Find columns this span occupies
    const occupiedCols = new Set<number>();
    for (let i = span.startIndex; i <= span.endIndex; i++) {
      occupiedCols.add(i % 7);
    }

    // Find first available track
    let assignedTrack = 0;
    let found = false;

    for (let t = 0; t < tracks.length; t++) {
      const trackCols = tracks[t];
      let hasConflict = false;

      for (const col of occupiedCols) {
        if (trackCols?.has(col)) {
          hasConflict = true;
          break;
        }
      }

      if (!hasConflict) {
        assignedTrack = t;
        found = true;
        break;
      }
    }

    // If no track found, create new one
    if (!found) {
      assignedTrack = tracks.length;
      tracks.push(new Set());
    }

    // Mark columns as occupied in this track
    const trackSet = tracks[assignedTrack];
    if (trackSet) {
      for (const col of occupiedCols) {
        trackSet.add(col);
      }
    }

    span.track = assignedTrack;
  }
}
